import {
  AlertTriangle,
  ArrowRight,
  ArrowUpCircle,
  Award,
  BarChart3,
  Compass,
  FileBarChart,
  History,
  LineChart,
  Microscope,
  PieChart,
  Rocket,
  Route,
  Shield,
  Sparkles,
  Trophy,
  Wrench,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Radar, RadarChart, PolarGrid, PolarAngleAxis } from "recharts";
import { cn } from "@/lib/utils";
import { getAllEvaluationRecords, getKindVisual } from "../mocks/evaluation-records";

/** Innovation Overview — align to `src/design.md` semantic tokens */

const radarRows = [
  // 更克制：同一品牌色为主，避免彩色条过多
  { subject: "研究方向", score: 85, tier: "良好", bar: "bg-primary" },
  { subject: "研究方法", score: 79, tier: "待提升", bar: "bg-primary/70" },
  { subject: "项目对标", score: 88, tier: "优势", bar: "bg-primary" },
  { subject: "项目综合", score: 80, tier: "待提升", bar: "bg-primary/70" },
  { subject: "创新生成", score: 84, tier: "良好", bar: "bg-primary" },
  { subject: "决策报告", score: 81, tier: "待提升", bar: "bg-primary/70" },
];

const radarChartData = radarRows.map((r) => ({
  subject: r.subject,
  A: r.score,
}));

const tierCls = (tier: string) => {
  // 避免大面积状态色：统一用中性色底，仅在文字上做轻强调
  if (tier === "优势") return "border border-border bg-muted/35 font-medium text-foreground";
  if (tier === "良好") return "border border-border bg-muted/35 font-medium text-foreground";
  return "border border-border bg-muted/35 font-medium text-muted-foreground";
};

const strengths: {
  title: string;
  score: number;
  desc: string;
  wrap: string;
  Icon: typeof BarChart3;
  icolor: string;
}[] = [
  {
    title: "项目对标评价",
    score: 88,
    desc: "查重与经费分析能力突出，立项成功率领先",
    wrap: "bg-muted/35",
    Icon: BarChart3,
    icolor: "text-primary",
  },
  {
    title: "研究方向评价",
    score: 85,
    desc: "战略前沿判断准确，与国家重大专项高度契合",
    wrap: "bg-accent",
    Icon: Compass,
    icolor: "text-primary",
  },
  {
    title: "创新生成系统",
    score: 84,
    desc: "Idea 产出质量高，跨领域融合创新能力强",
    wrap: "bg-muted/35",
    Icon: Sparkles,
    icolor: "text-primary",
  },
  {
    title: "创新决策报告",
    score: 81,
    desc: "决策报告结构完整，风险等级标注准确",
    wrap: "bg-muted/35",
    Icon: FileBarChart,
    icolor: "text-primary",
  },
];

const gaps: {
  title: string;
  score: number;
  desc: string;
  wrap: string;
  Icon: typeof Microscope;
  icolor: string;
}[] = [
  {
    title: "研究方法评价",
    score: 79,
    desc: "技术指标量化分析有待加强，建议增加消融实验",
    wrap: "bg-muted/35",
    Icon: Microscope,
    icolor: "text-primary",
  },
  {
    title: "项目综合评价",
    score: 80,
    desc: "项目申报书结构优化空间较大，创新点表述需聚焦",
    wrap: "bg-muted/35",
    Icon: Award,
    icolor: "text-primary",
  },
];

const trendPts = [
  { m: "11月", v: 68 },
  { m: "12月", v: 73 },
  { m: "1月", v: 76 },
  { m: "2月", v: 79 },
  { m: "3月", v: 78 },
  { m: "4月", v: 82 },
];

const actions: {
  title: string;
  badge: string;
  badgeCls: string;
  desc: string;
  tag: string;
  bg: string;
  iconBg: string;
  Icon: typeof Rocket;
  icolor: string;
}[] = [
  {
    title: "启动量子材料方向专利布局",
    badge: "优先",
    badgeCls: "border border-border bg-muted/35 text-foreground",
    desc: "当前研究成熟度已达申请阈值，建议优先推进核心技术的专利申请",
    tag: "专利申请分析",
    bg: "bg-muted/20",
    iconBg: "bg-muted/35",
    Icon: Rocket,
    icolor: "text-primary",
  },
  {
    title: "申报国家自然科学基金重点项目",
    badge: "优先",
    badgeCls: "border border-border bg-muted/35 text-foreground",
    desc: "研究方向战略匹配度 91 分，当前窗口期最适合冲击重点项目",
    tag: "项目申报分析",
    bg: "bg-muted/20",
    iconBg: "bg-muted/35",
    Icon: Trophy,
    icolor: "text-primary",
  },
  {
    title: "补充研究方法的消融实验设计",
    badge: "建议",
    badgeCls: "border border-border bg-muted/35 text-muted-foreground",
    desc: "技术指标评分偏低（79分），建议在现有方法上增加对照实验",
    tag: "技术指标分析",
    bg: "bg-muted/20",
    iconBg: "bg-muted/35",
    Icon: Wrench,
    icolor: "text-primary",
  },
  {
    title: "探索计算神经科学交叉方向",
    badge: "建议",
    badgeCls: "border border-border bg-muted/35 text-muted-foreground",
    desc: "已有 Bayesian 基础可迁移，交叉创新评估潜力得分高达 89 分",
    tag: "交叉领域拓展",
    bg: "bg-muted/20",
    iconBg: "bg-muted/35",
    Icon: Route,
    icolor: "text-primary",
  },
];

const recentRecords = getAllEvaluationRecords()
  .slice()
  .sort((a, b) => (a.date === b.date ? b.score - a.score : b.date.localeCompare(a.date)))
  .slice(0, 5);

const usageRows = [
  { label: "研究方向评价", Icon: Compass, color: "hsl(var(--primary))", count: 13, avg: "均86", avgTone: "text-primary", pct: 27.66 },
  { label: "创新生成系统", Icon: Sparkles, color: "hsl(var(--primary))", count: 11, avg: "均88", avgTone: "text-primary", pct: 23.4 },
  { label: "项目综合评价", Icon: Award, color: "hsl(var(--primary))", count: 9, avg: "均77", avgTone: "text-muted-foreground", pct: 19.15 },
  { label: "项目对标评价", Icon: BarChart3, color: "hsl(var(--primary))", count: 7, avg: "均83", avgTone: "text-primary", pct: 14.89 },
  { label: "创新决策报告", Icon: FileBarChart, color: "hsl(var(--primary))", count: 4, avg: "均84", avgTone: "text-primary", pct: 8.51 },
  { label: "研究方法评价", Icon: Microscope, color: "hsl(var(--primary))", count: 3, avg: "均85", avgTone: "text-primary", pct: 6.38 },
];

const dimensionCards = [
  {
    title: "研究方向评价",
    desc: "聚焦科研方向的顶层战略研判",
    tags: ["出口管制分析", "前沿问题分析", "+2"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: Compass,
    icolor: "text-primary",
  },
  {
    title: "研究方法评价",
    desc: "针对科研方法的技术可行性与科学性进行评估",
    tags: ["技术指标分析"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: Microscope,
    icolor: "text-primary",
  },
  {
    title: "项目对标评价",
    desc: "科研项目立项、执行阶段的横向对标与合理性校验",
    tags: ["项目查重分析", "经费合理性分析"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: BarChart3,
    icolor: "text-primary",
  },
  {
    title: "项目综合评价",
    desc: "覆盖科研项目全流程的多场景综合评审",
    tags: ["项目申报分析", "论文审稿分析", "+1"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: Award,
    icolor: "text-primary",
  },
  {
    title: "创新生成系统",
    desc: "面向科研创新的主动生成与决策支持",
    tags: ["Idea头脑风暴", "交叉领域拓展", "+4"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: Sparkles,
    icolor: "text-primary",
  },
  {
    title: "创新决策报告",
    desc: "为最终决策提供结构化输出",
    tags: ["创新多维评分", "研究风险等级", "+2"],
    wrap: "bg-muted/35",
    tcolor: "text-foreground",
    Icon: FileBarChart,
    icolor: "text-primary",
  },
];

export default function InnovationOverview() {
  return (
    <div className="min-h-full pb-4">
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col justify-between rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                <div className="flex h-14 w-14 items-center justify-center rounded-full border border-border bg-muted/35 text-xl font-bold text-foreground">
                  A
                </div>
                <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                  <Sparkles className="h-2.5 w-2.5 text-white" />
                </div>
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[15px] font-semibold text-foreground">张研究员</span>
                  <span className="whitespace-nowrap rounded-full border border-border bg-muted/35 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    高级研究员
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  主研领域：<span className="font-medium text-foreground">量子信息与材料</span>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  研究风格：<span className="font-medium text-foreground">理论驱动型</span>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[11px] text-muted-foreground">综合创新能力指数</span>
                <span className="text-[18px] font-bold text-primary">
                  82<span className="ml-0.5 text-[11px] font-normal text-muted-foreground">/100</span>
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary transition-all" style={{ width: "82%" }} />
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-[10px] text-muted-foreground">
                  累计评价 <span className="font-semibold text-foreground">47</span> 次
                </span>
                <span className="text-[10px] text-muted-foreground">
                  本月新增 <span className="font-semibold text-foreground">8</span> 次
                </span>
                <span className="text-[10px] text-muted-foreground">
                  综合报告 <span className="font-semibold text-foreground">12</span> 份
                </span>
              </div>
            </div>
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-1.5 text-[10px] text-muted-foreground">涉猎领域</div>
              <div className="flex flex-wrap gap-1">
                <span className="rounded-full border border-border bg-muted/35 px-2 py-0.5 text-[10px] font-medium text-foreground">量子信息与材料</span>
                {["机器学习", "电路仿真", "新能源材料"].map((t) => (
                  <span key={t} className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-sm lg:col-span-2">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-[13px] font-semibold text-foreground">创新能力维度分布</span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[10px] text-muted-foreground">基于近 47 次评价</span>
            </div>
            <div className="flex flex-col items-stretch gap-6 md:flex-row md:items-center md:gap-8">
              <div className="mx-auto flex-shrink-0 rounded-2xl p-3 md:mx-0" style={{ width: 190, height: 190 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarChartData}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                    {/* 雷达图本体：淡蓝（降低线/面不透明度） */}
                    <Radar
                      name="score"
                      dataKey="A"
                      // 直接使用淡蓝 rgba，避免主题变量导致“看不出变化”
                      stroke="rgba(61,105,232,0.9)"
                      strokeWidth={1.6}
                      fill="rgba(61,105,232,0.22)"
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              <div className="min-w-0 flex-1 space-y-2.5">
                {radarRows.map((r) => (
                  <div key={r.subject}>
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-[12px] font-medium text-muted-foreground">{r.subject}</span>
                      <div className="flex items-center gap-2">
                        <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-medium", tierCls(r.tier))}>{r.tier}</span>
                        <span className="w-6 text-right text-[13px] font-bold text-foreground">{r.score}</span>
                      </div>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div className={cn("h-full rounded-full", r.bar)} style={{ width: `${r.score}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                <Shield className="h-[13px] text-primary" />
              </div>
              <span className="text-[13px] font-semibold text-foreground">核心优势</span>
            </div>
            <div className="space-y-3">
              {strengths.map((s) => (
                <div key={s.title} className="flex items-start gap-2.5">
                  <div className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg", s.wrap)}>
                    <s.Icon className={cn("h-[13px] w-[13px]", s.icolor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-foreground">{s.title}</span>
                      <span className="text-[12px] font-bold text-primary">{s.score}</span>
                    </div>
                    <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                <ArrowUpCircle className="h-[13px] text-primary" />
              </div>
              <span className="text-[13px] font-semibold text-foreground">提升空间</span>
            </div>
            <div className="space-y-3">
              {gaps.map((g) => (
                <div key={g.title} className="flex items-start gap-2.5">
                  <div className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg", g.wrap)}>
                    <g.Icon className={cn("h-[13px] w-[13px]", g.icolor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-foreground">{g.title}</span>
                      <span className="text-[12px] font-bold text-primary">{g.score}</span>
                    </div>
                    <p className="mt-0.5 text-[10px] leading-relaxed text-muted-foreground">{g.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2 border-t border-border pt-3">
              <div className="mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3 text-muted-foreground" />
                <span className="text-[11px] font-semibold text-foreground">风险提示</span>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 p-2">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  拓扑材料研究涉及潜在出口管制条款，建议在发表前完成合规审查
                </p>
              </div>
              <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/20 p-2">
                <div className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                <p className="text-[10px] leading-relaxed text-muted-foreground">
                  近期 3 项评价处于优化中状态，建议优先完成多相户平台项目的申报书修订
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                  <LineChart className="h-[13px] text-primary" />
                </div>
                <span className="text-[13px] font-semibold text-foreground">评分趋势</span>
              </div>
              <span className="text-[10px] text-muted-foreground">近6个月</span>
            </div>
            <div className="h-20 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trendPts} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="invTrendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="rgba(61,105,232,0.28)" stopOpacity={1} />
                      <stop offset="100%" stopColor="rgba(61,105,232,0)" stopOpacity={1} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="rgba(61,105,232,0.9)"
                    strokeWidth={1.5}
                    fill="url(#invTrendGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-primary/20 bg-transparent p-2.5 text-center">
                <div className="text-[18px] font-bold text-primary">+11</div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">半年提升</div>
              </div>
              <div className="rounded-lg border border-primary/20 bg-transparent p-2.5 text-center">
                <div className="text-[18px] font-bold text-primary">+3</div>
                <div className="mt-0.5 text-[10px] text-muted-foreground">本月提升</div>
              </div>
            </div>
            <div className="mt-3 flex items-center gap-1.5">
              <span className="text-[10px] text-muted-foreground">持续评价频率是提分的关键因子</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                <Route className="h-[13px] text-primary" />
              </div>
              <span className="text-[13px] font-semibold text-foreground">推荐行动路径</span>
              <span className="rounded-full border border-border bg-muted/20 px-2 py-0.5 text-[10px] text-muted-foreground">基于你的评价数据智能生成</span>
            </div>
            <Link to="/innovation/create" className="flex cursor-pointer items-center gap-1 text-[12px] text-primary hover:opacity-80">
              新建评价 <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {actions.map((item) => (
              <Link
                key={item.title}
                to="/innovation/create"
                className={cn(
                  "group flex cursor-pointer items-start gap-3 rounded-xl border border-border p-3.5 transition-all hover:bg-accent",
                  item.bg,
                )}
              >
                <div className={cn("flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg", item.iconBg)}>
                  <item.Icon className={cn("h-[15px] w-[15px]", item.icolor)} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-[12px] font-semibold leading-snug text-foreground">{item.title}</span>
                    <span className={cn("whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-medium", item.badgeCls)}>
                      {item.badge}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{item.desc}</p>
                  <div className="mt-2 flex items-center gap-1">
                    <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">{item.tag}</span>
                    <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm lg:col-span-3">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                  <History className="h-[13px] text-primary" />
                </div>
                <span className="text-[13px] font-semibold text-foreground">最近评价</span>
              </div>
              <Link to="/innovation/records" className="flex cursor-pointer items-center gap-0.5 text-[12px] text-primary hover:opacity-80">
                查看全部 <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="space-y-1.5">
              {recentRecords.map((r) => {
                const v = getKindVisual(r.kind);
                const Icon = v.Icon;
                const scoreTone = r.status === "优化中" ? "text-muted-foreground" : "text-primary";
                const statusCls =
                  r.status === "优化中"
                    ? "border border-border bg-background text-muted-foreground"
                    : "border border-border bg-background text-foreground";
                return (
                <Link
                  key={r.title}
                  to={`/innovation/records/${r.id}`}
                  className="group flex cursor-pointer items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-accent"
                >
                  <div className={cn("flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg", v.wrap)}>
                    <Icon className={cn("h-[14px] w-[14px]", v.icolor)} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[12px] font-medium text-foreground">{r.title}</span>
                      <span className={cn("flex-shrink-0 rounded-full px-1.5 py-0.5 text-[9px]", statusCls)}>{r.status}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">{r.kind}</span>
                      <span className="text-muted-foreground/40">·</span>
                      <span className="text-[10px] text-muted-foreground">{r.date}</span>
                    </div>
                  </div>
                  <div className={cn("flex-shrink-0 text-[14px] font-bold", scoreTone)}>{r.score}</div>
                  <ArrowRight className="h-[15px] w-[15px] flex-shrink-0 text-muted-foreground/40 transition-colors group-hover:text-muted-foreground" />
                </Link>
              );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 shadow-sm lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-muted/35">
                <PieChart className="h-[13px] text-primary" />
              </div>
              <span className="text-[13px] font-semibold text-foreground">维度使用分布</span>
            </div>
            <div className="space-y-2.5">
              {usageRows.map((u) => (
                <div key={u.label}>
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <u.Icon className="h-[11px] w-[11px]" style={{ color: u.color }} />
                      <span className="text-[11px] text-muted-foreground">{u.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-muted-foreground">{u.count}次</span>
                      <span className={cn("text-[10px] font-semibold", u.avgTone)}>{u.avg}</span>
                    </div>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${u.pct}%`, background: u.color, opacity: 0.7 }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3">
              <div className="text-center">
                <div className="text-[18px] font-bold text-foreground">82</div>
                <div className="text-[10px] text-muted-foreground">综合均分</div>
              </div>
              <div className="text-center">
                <div className="text-[18px] font-bold text-foreground">3</div>
                <div className="text-[10px] text-muted-foreground">待优化项</div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[13px] font-semibold text-foreground">可用评价维度</span>
            <Link to="/innovation/create" className="flex cursor-pointer items-center gap-0.5 text-[12px] text-primary hover:opacity-80">
              新建评价 <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
            {dimensionCards.map((d) => (
              <Link
                key={d.title}
                to={`/innovation/create?dimension=${encodeURIComponent(d.title)}`}
                className="group rounded-2xl border border-border bg-card p-4 shadow-sm transition-all hover:bg-accent"
              >
                <div className={cn("mb-2.5 flex h-9 w-9 items-center justify-center rounded-lg", d.wrap)}>
                  <d.Icon className={cn("h-[17px] w-[17px]", d.icolor)} />
                </div>
                <div className="mb-1 text-[12px] font-semibold text-foreground">{d.title}</div>
                <div className="mb-2 text-[10px] leading-relaxed text-muted-foreground">{d.desc}</div>
                <div className="flex flex-wrap gap-1">
                  {d.tags.map((t) => (
                    <span
                      key={t}
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[9px]",
                        t.startsWith("+") ? "border border-border bg-muted/20 text-muted-foreground" : cn(d.wrap, d.tcolor),
                      )}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
